'use client'

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { renderHeading } from '@/utilities/renderHeading'

interface AddressSuggestion {
  sla: string
  score: number
  pid: string
}

export const SubFooterClient: React.FC<{
  heading: string
  description: string
  placeholder?: string
  buttonLabel: string
  hideBorder?: boolean
  background?: 'cyan' | 'white'
}> = ({
  heading,
  description,
  placeholder = 'Enter your property address',
  buttonLabel,
  hideBorder,
  background = 'white',
}) => {
  const router = useRouter()
  const pathname = usePathname()
  const [address, setAddress] = useState('')
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const fetchSuggestions = useCallback((query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (query.length < 3) {
      setSuggestions([])
      setIsOpen(false)
      return
    }

    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/address-lookup?q=${encodeURIComponent(query)}`)
        const data: AddressSuggestion[] = await res.json()
        setSuggestions(data)
        setIsOpen(data.length > 0)
        setHighlightIndex(-1)
      } catch {
        setSuggestions([])
        setIsOpen(false)
      }
    }, 300)
  }, [])

  const handleSelect = (sla: string) => {
    setAddress(sla)
    setSuggestions([])
    setIsOpen(false)
  }

  const handleSubmit = () => {
    if (!address.trim()) return
    router.push(`/appraisal/step-2?address=${encodeURIComponent(address)}`)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIndex((prev) => Math.min(prev + 1, suggestions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIndex((prev) => Math.max(prev - 1, -1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightIndex >= 0 && suggestions[highlightIndex]) {
        handleSelect(suggestions[highlightIndex].sla)
      } else if (address.trim()) {
        handleSubmit()
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  // Hide on appraisal pages to avoid duplicate address input
  if (pathname.startsWith('/appraisal')) return null

  // Page-specific heading overrides — the SubFooter heading is a single global field shared
  // by every page, but some pages (e.g. Commercial, where "Home" doesn't fit) need different
  // wording without changing it site-wide.
  const HEADING_OVERRIDES: Record<string, string> = {
    '/commercial': 'What’s *Your Property* Worth?',
    '/commercial-sold': 'What’s *Your Property* Worth?',
    '/leased': 'What’s *Your Property* Worth?',
  }
  const resolvedHeading = HEADING_OVERRIDES[pathname] ?? heading

  return (
    // Figma CTA Block 87:7425 defines a single surface: theme/background #ffffff with a
    // 1px theme/border top rule. There is no cyan variant in the component set, so this
    // component always renders the white/bordered treatment regardless of the stored
    // `background` field value (see AppraisalCTA/Component.tsx comment + parity escalation).
    // px-16 (64) gutters, pb-32 (section/xs) / pt-0. Content max-w-1600, centred,
    // with a 1px #e0dfe0 top border (suppressed for the in-page AppraisalCTA via hideBorder).
    <section className="bg-background">
      <div className="mx-auto max-w-[var(--container-content)] px-[var(--spacing-container)] pb-[var(--spacing-section-xs)]">
        {/* Figma splits into two flex-1 columns from TABLET up (4022:98689), not desktop —
            at 768 the heading sits beside the description, not above it. */}
        <div
          className={`flex flex-col md:flex-row gap-[var(--spacing-container)] items-start pt-[var(--spacing-section-xs)] ${
            hideBorder ? '' : 'border-t border-border'
          }`}
        >
          {/* Left column — heading. Figma Headings/headlineMd is mode-responsive
              (32/40 desktop · 25/34 tablet · 24/28 mobile, tracking -0.3px), which is exactly
              what `text-display-xl` expresses — do not pin it to a literal 32/40.
              Words wrapped in *asterisks* render italic (Merriweather italic emphasis,
              e.g. "What's *Your Home* Worth?") via the shared renderHeading util. */}
          <h2 className="flex-1 font-display text-display-xl font-normal text-brand">
            {renderHeading(resolvedHeading)}
          </h2>

          {/* Right column — body + input/button row, gap-24 */}
          <div className="flex flex-1 flex-col gap-6">
            {/* Figma Body/bodyMd: Lato Light 300, 16px with line-height bound to font/size/lg
                (20 mobile / 22 tablet / 24 desktop) — `text-body-md`, not a fixed leading. */}
            <p className="font-base text-body-md font-light text-foreground">{description}</p>

            {/* Figma stacks the input above the button below desktop (4022:100857 mobile /
                4022:98698 tablet — flex-col, gap/gap-1, items-start) and only sets them
                side-by-side on desktop (79:21734). */}
            <div className="flex w-full flex-col items-start gap-1 lg:flex-row">
              <div ref={wrapperRef} className="relative w-full min-w-0 lg:flex-1">
                <input
                  type="text"
                  value={address}
                  onChange={(e) => {
                    setAddress(e.target.value)
                    fetchSuggestions(e.target.value)
                  }}
                  onKeyDown={handleKeyDown}
                  onFocus={() => {
                    if (suggestions.length > 0) setIsOpen(true)
                  }}
                  placeholder={placeholder}
                  autoComplete="off"
                  // Figma `Trigger` 4095:83847 — muted bg #e0dfe0, no visible border,
                  // radius sm, shadow-xs, h-9, px-3; placeholder theme/FOREGROUND
                  // (black) Lato light 14/16, 16px lucide search icon at the right.
                  className="w-full h-9 px-3 pr-10 bg-muted rounded-[2px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] text-body-sm font-light text-foreground placeholder:text-foreground focus:outline-none focus-visible:ring-[3px] focus-visible:ring-[#8cc640]/50 focus-visible:ring-offset-0"
                />
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-foreground"
                />
                {isOpen && suggestions.length > 0 && (
                  <ul className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-border shadow-md max-h-64 overflow-y-auto rounded-sm">
                    {suggestions.map((suggestion, i) => (
                      <li
                        key={suggestion.pid}
                        onMouseDown={(e) => {
                          e.preventDefault()
                          handleSelect(suggestion.sla)
                        }}
                        onMouseEnter={() => setHighlightIndex(i)}
                        className={`px-3 py-2.5 text-body-sm cursor-pointer text-left ${
                          i === highlightIndex
                            ? // Figma menu item 2198:36854 highlighted state: theme/secondary fill, theme/foreground text
                              'bg-secondary text-foreground'
                            : 'text-brand hover:bg-secondary'
                        }`}
                      >
                        {suggestion.sla}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {/* Figma CTA Block 79:21734 → Button type=primary size=Regular (2198:34409):
                  accent fill, black label, h36/px24, radius sm, NO shadow (shadow/xs is
                  declared on the outline type only). Canonical component owns all of it. */}
              <Button onClick={handleSubmit} className="shrink-0">
                {buttonLabel}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
